import { useEffect, useRef } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { useSiteBanner } from "../../hooks/useSiteBanner";
import { SITE_BANNER_HEIGHT } from "../layout/SiteBanner";
import { useNateM, NATE_M_EXPANDED_WIDTH, NATE_M_MINIMIZED_WIDTH } from "../../context/NateMContext";

export function HorizontalScrollStrip() {
  const { user } = useAuth();
  const banner = useSiteBanner();
  const { minimized } = useNateM();
  const stripRef = useRef(null);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onWheel = (e) => {
      const rows = Array.from(document.querySelectorAll("[data-hscroll]"));
      const topOffset = el.getBoundingClientRect().top;
      const target = rows.find((row) => {
        const rect = row.getBoundingClientRect();
        return rect.bottom > topOffset && rect.top < window.innerHeight && row.scrollWidth > row.clientWidth;
      });
      if (!target) return;
      e.preventDefault();
      target.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Hovering directly over a card tray: scroll it horizontally instead of the page vertically.
  useEffect(() => {
    const onWheel = (e) => {
      const tray = e.target.closest && e.target.closest("[data-hscroll]");
      if (!tray || tray.scrollWidth <= tray.clientWidth) return;
      e.preventDefault();
      tray.scrollLeft += e.deltaY;
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  // Click-and-drag inside a card tray: drag left/right to scroll, like a two-finger swipe.
  // preventDefault on mousedown (not just mousemove) stops the browser's native text-selection
  // drag from ever starting — that selection highlight was what made the tray look "broken"/
  // cut-off, since dragging across card text painted the whole page in a blue selection overlay.
  useEffect(() => {
    let drag = null;
    let suppressTimeout = null;
    const suppressNextClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      window.removeEventListener("click", suppressNextClick, true);
    };
    const onMouseDown = (e) => {
      const tray = e.target.closest && e.target.closest("[data-hscroll]");
      if (!tray || tray.scrollWidth <= tray.clientWidth) return;
      drag = { tray, startX: e.clientX, startScrollLeft: tray.scrollLeft, moved: false };
      document.body.classList.add("hscroll-drag-active");
      e.preventDefault();
    };
    const onMouseMove = (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      if (Math.abs(dx) > 5) {
        drag.moved = true;
        drag.tray.classList.add("hscroll-dragging");
      }
      if (drag.moved) {
        drag.tray.scrollLeft = drag.startScrollLeft - dx;
        e.preventDefault();
      }
    };
    const onMouseUp = () => {
      if (drag?.moved) {
        drag.tray.classList.remove("hscroll-dragging");
        window.addEventListener("click", suppressNextClick, true);
        clearTimeout(suppressTimeout);
        suppressTimeout = setTimeout(() => window.removeEventListener("click", suppressNextClick, true), 150);
      }
      document.body.classList.remove("hscroll-drag-active");
      drag = null;
    };
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      clearTimeout(suppressTimeout);
      document.body.classList.remove("hscroll-drag-active");
    };
  }, []);

  if (!user || (user.role !== "admin" && user.role !== "manager")) return null;

  const topOffset = SITE_BANNER_HEIGHT(user, banner);
  const right = minimized ? NATE_M_MINIMIZED_WIDTH : NATE_M_EXPANDED_WIDTH;

  return (
    <div
      ref={stripRef}
      data-testid="rep-card-scroll-strip"
      style={{ top: topOffset, right }}
      title="Hover here and scroll to move the rep cards left/right"
      className="fixed bottom-0 w-6 bg-gradient-to-b from-igc-gold/50 via-igc-gold/25 to-igc-gold/50 hover:from-igc-gold/70 hover:via-igc-gold/40 hover:to-igc-gold/70 border-l border-igc-gold/40 z-30 flex flex-col items-center py-4 cursor-ns-resize transition-colors"
    >
      <CaretUp size={11} weight="bold" className="text-igc-goldText/80 shrink-0" />
      <div className="flex-1 w-1 my-2 bg-igc-goldText/25 rounded-full" />
      <CaretDown size={11} weight="bold" className="text-igc-goldText/80 shrink-0" />
    </div>
  );
}
